import routeConfig from '../routes/routeConfig';

const BRAND_SUFFIX = 'HireVeda';

/** Routes defined outside routeConfig (routeDefinitions.tsx, etc.) */
const EXTRA_ROUTE_TITLES = {
  '/terms': 'Terms & Conditions',
  '/NewExecedge': 'Industry Outreach',
  '/NewExecedge/self-reflection-form': 'Self Reflection Form',
  '/NewExecedge/project-timeline': 'Project Timeline',
  '/NewExecedge/industry-outreach': 'Industry Outreach',
  '/NewExecedge/personal-network': 'Personal Network',
  '/admin/execedge-dashboard': 'ExecEdge Dashboard',
  '/admin/execedge-clients': 'ExecEdge Clients',
  '/admin/automate-emails': 'Automate Emails',
  '/admin/role-search': 'Role Search Lab',
  '/NewExecedge/dms': 'DMs',
  '/NewExecedge/recruiter-network': 'Recruiter Network',
  '/NewExecedge/vc-pe-network': 'VC/PE Network',
  '/NewExecedge/resources': 'Resources',
  '/NewExecedge/interview-brief-request': 'Interview Brief Request',
  '/interview-brief-request': 'Interview Brief Request',
  '/execedge/interview-brief-request': 'Interview Brief Request',
  '/execedge/interview-briefs': 'Interview Briefs',
  '/execedge/compensation-briefs': 'Compensation Briefs',
  '/execedge/client-ops': 'Client Ops',
  '/execedge/tracker-candidates': 'Tracker Candidates',
  '/execedge/pipeline-report': 'ExecEdge Pipeline Report',
  '/execedge/all-jobs': 'All Jobs',
  '/execedge/company-uploader': 'Company Uploader',
  '/execedge/dms-uploader': 'DMs Uploader',
  '/execedge/uploader': 'Uploader',
  '/execedge/uploader/company': 'Company Uploader',
  '/execedge/uploader/dms': 'DMs Uploader',
  '/execedge-comms': 'ExecEdge Comms',
  '/candidate/applications': 'Applications',
  '/hv/jobs': 'Jobs',
  '/jobs': 'Jobs',
  '/resume-builder': 'Resume Builder',
  '/payment': 'Payment',
  '/payment-dashboard': 'Payment Dashboard',
  '/invoices': 'Invoices',
  '/eedge': 'ExecEdge',
  '/eedge/predemo': 'Pre Demo',
  '/eedge/thank-you': 'Thank You',
  '/eedge/faqs': 'FAQs',
  '/eedge/feedback': 'Feedback',
  '/eedge/success': 'Success',
  '/eedge/demo': 'Demo',
  '/eedge/program-plan': 'Program Plan',
  '/eedge/program-plan-test': 'Payment Test',
  '/eedge/payment': 'ExecEdge Payment',
  '/salesfunnel': 'Sales Funnel',
  '/ai-flow': 'AI Flow',
  '/whatsapp/conversations': 'WhatsApp Conversations',
  '/whatsapp/contacts': 'WhatsApp Contacts',
  '/whatsapp/templates': 'WhatsApp Templates',
  '/whatsapp/queue': 'WhatsApp Queue',
  '/whatsapp/events': 'WhatsApp Events',
  '/whatsapp/health': 'WhatsApp Health',
  '/auth/google/callback': 'Signing in',
  '/join-whatsapp': 'Join WhatsApp',
  '/team-register': 'Team Profile',
  '/team': 'Team',
  '/l': 'Redirecting',
};

/** Fallback when route has no menu.name */
const KEY_TITLES = {
  NewExecedge: 'Industry Outreach',
  NewExecedgeView: 'Industry Outreach',
  NewExecedgeResourceShare: 'Resources',
  NewExecedgeInterviewBriefRequest: 'Interview Brief Request',
  OutreachTrackerPage: 'ExecEdge Dashboard',
  'admin-execedge-dashboard': 'ExecEdge Dashboard',
  ExecEdgeClientsOpsPage: 'ExecEdge Clients',
  'admin-execedge-clients': 'ExecEdge Clients',
  'admin-role-search': 'Role Search Lab',
  InterviewBriefRequestRedirect: 'Interview Brief Request',
  ExecedgeInterviewBriefRequest: 'Interview Brief Request',
  CandidateApplicationsPage: 'Applications',
  Pipeline: 'Tracker',
  Candidates: 'Candidate Search',
  CandidateDashboardMain: 'Profile',
  EditProfile: 'Edit Profile',
  RecoverPassword: 'Recover Password',
  OAuthCallback: 'Signing in',
  Apply: 'Apply',
  EditCandidates: 'Edit Candidate',
  EmailValidationDashboard: 'Email Validation',
  NormalizationDashboard: 'Normalization',
  MasterDataDashboard: 'Master Data',
  CandidateCorrectionDashboard: 'Candidate Corrections',
  WorkerDetail: 'Worker',
  CandidateEditPage: 'Edit Candidate',
  ExecEdgeFunnel: 'Sales Funnel',
  ExecEdgeCommsDashboard: 'ExecEdge Comms',
  'hv-crm-company-details': 'Details',
};

const PATH_SEGMENT_TITLES = {
  tracker: 'Tracker',
  roles: 'Roles',
  candidates: 'Candidates',
  search: 'Search',
  signals: 'Signals',
  profile: 'Profile',
  dashboard: 'Dashboard',
  'short-links': 'Short Links',
  merge: 'Database',
  normalization: 'Normalization',
  'master-data': 'Master Data',
  'candidate-corrections': 'Candidate Corrections',
  workers: 'Workers',
  masters: 'Masters',
  jobs: 'Jobs',
  fin360: 'Candidate Form',
  'role-sanitize': 'Role Management',
};

function normalizePath(path) {
  if (!path || path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

function humanizeSegment(segment) {
  if (!segment) return 'Home';
  if (PATH_SEGMENT_TITLES[segment]) return PATH_SEGMENT_TITLES[segment];
  return segment
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function titleFromPath(path) {
  const segments = path.split('/').filter(Boolean);
  if (!segments.length) return 'Home';

  const last = segments[segments.length - 1];
  if (/^[a-f0-9]{24}$/i.test(last) || /^[0-9a-f-]{20,}$/i.test(last)) {
    const parent = segments[segments.length - 2];
    return parent ? humanizeSegment(parent) : 'Page';
  }

  return humanizeSegment(last);
}

function collectFromRouteConfig(routes, basePath = '') {
  const entries = [];

  for (const route of routes) {
    const segment = route.path ?? '';
    let fullPath = `${basePath}${segment}`.replace(/\/+/g, '/');
    if (fullPath.length > 1 && fullPath.endsWith('/')) {
      fullPath = fullPath.slice(0, -1);
    }

    const menuPath =
      route.menu?.path?.startsWith('/') &&
      !route.menu.path.startsWith('http') &&
      route.menu.path !== '/'
        ? normalizePath(route.menu.path)
        : normalizePath(fullPath);

    const title =
      route.menu?.name ||
      KEY_TITLES[route.key] ||
      (route.component && fullPath ? titleFromPath(fullPath) : null);

    if (title && menuPath) {
      entries.push({ path: menuPath, title, hasParams: segment.includes(':') });
    }

    if (route.children) {
      const childBase = fullPath === '/' ? '' : fullPath;
      entries.push(...collectFromRouteConfig(route.children, childBase));
    }
  }

  return entries;
}

function buildTitleEntries() {
  const byPath = new Map();

  const add = (path, title) => {
    const key = normalizePath(path);
    if (key && title && !byPath.has(key)) byPath.set(key, title);
  };

  collectFromRouteConfig(routeConfig).forEach(({ path, title }) => add(path, title));
  Object.entries(EXTRA_ROUTE_TITLES).forEach(([path, title]) => add(path, title));

  const exact = [];
  const patterns = [];

  for (const [path, title] of byPath) {
    if (path.includes(':')) {
      patterns.push({ path, title });
    } else {
      exact.push({ path, title });
    }
  }

  exact.sort((a, b) => b.path.length - a.path.length);
  patterns.sort((a, b) => b.path.length - a.path.length);

  return { exact, patterns };
}

const { exact: exactEntries, patterns: patternEntries } = buildTitleEntries();

export function getPageTitle(pathname) {
  const path = normalizePath(pathname);

  const exactHit = exactEntries.find((e) => e.path === path);
  if (exactHit) return exactHit.title;

  for (const { path: pattern, title } of patternEntries) {
    const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`);
    if (regex.test(path)) return title;
  }

  for (const { path: prefix, title } of exactEntries) {
    if (path.startsWith(`${prefix}/`)) return title;
  }

  if (path.startsWith('/apply/')) return 'Apply';
  if (path.startsWith('/eedge/')) return 'ExecEdge';
  if (path.startsWith('/ai-flow/')) return 'AI Flow';
  if (path.startsWith('/resume-builder/')) return 'Resume Builder';
  if (path.startsWith('/whatsapp/conversations/')) return 'Conversation';
  if (path.startsWith('/whatsapp/contacts/')) return 'Contact';

  return titleFromPath(path);
}

export function formatDocumentTitle(pageTitle) {
  if (!pageTitle) return BRAND_SUFFIX;
  return `${pageTitle} | ${BRAND_SUFFIX}`;
}

/** New ExecEdge browser tab: "{section} | ExecEdge". */
export function setExecEdgeDocumentTitle(pageTitle) {
  if (typeof document === 'undefined') return;
  document.title = pageTitle ? `${pageTitle} | ExecEdge` : 'ExecEdge';
}

export function updateDocumentTitle(pathname) {
  if (typeof document === 'undefined') return;
  const path = normalizePath(pathname);
  const pageTitle = getPageTitle(pathname);
  if (path === '/NewExecedge' || path.startsWith('/NewExecedge/')) {
    setExecEdgeDocumentTitle(pageTitle);
    return;
  }
  document.title = formatDocumentTitle(pageTitle);
}
